import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULTS, useCms } from "@/lib/cms";
import { useSiteKnowledge } from "@/lib/siteKnowledge";
import { applySeoHead } from "@/lib/seoHead";
import { api } from "@/lib/api";
import { usePortfolioProject } from "@/lib/portfolioCms";

export default function SeoDocumentHead() {
  const { cms } = useCms();
  const { knowledge } = useSiteKnowledge();
  const location = useLocation();
  const [graphs, setGraphs] = useState([]);
  const [docSeo, setDocSeo] = useState({});
  const rawPath = location.pathname || "/";
  const path = rawPath.length > 1 && rawPath.endsWith("/") ? rawPath.slice(0, -1) : rawPath;
  const portfolioSlug = path.startsWith("/portfolio/") ? path.replace("/portfolio/", "").split("/")[0] : "";
  const { project: cmsProject } = usePortfolioProject(portfolioSlug);

  const pageSeo = useMemo(
    () => ({
      ...((knowledge.pages || []).find((row) => row.path === path) || {}),
      ...docSeo,
    }),
    [knowledge.pages, path, docSeo],
  );

  const projectSeo = useMemo(() => {
    if (!portfolioSlug || !cmsProject) return {};
    return {
      seo_title: cmsProject.seo_title || (cmsProject.title ? `${cmsProject.title} | DSITESOLUTION` : ""),
      seo_description: cmsProject.seo_description || cmsProject.shortDescription,
      og_title: cmsProject.og_title,
      og_description: cmsProject.og_description,
      og_image: cmsProject.og_image || cmsProject.cardImage,
      canonical: cmsProject.canonical,
      robots: cmsProject.robots,
    };
  }, [portfolioSlug, cmsProject]);

  useEffect(() => {
    let cancelled = false;
    if (path.startsWith("/design-preview")) {
      setDocSeo({ robots: "noindex,nofollow", seo_title: "Design Preview (DEV)" });
      setGraphs([]);
      return () => {
        cancelled = true;
      };
    }
    if (
      path.startsWith("/guides") ||
      path.startsWith("/services") ||
      path === "/capabilities" ||
      path === "/portfolio"
    ) {
      api
        .get("/public/search-document", { params: { path }, timeout: 8000 })
        .then((res) => {
          if (!cancelled) setDocSeo(res.data?.seo || {});
        })
        .catch(() => {
          if (!cancelled) setDocSeo({});
        });
    } else {
      setDocSeo({});
    }
    api
      .get("/public/jsonld", { params: { path }, timeout: 8000 })
      .then((res) => {
        if (!cancelled) setGraphs(res.data?.graphs || []);
      })
      .catch(() => {
        if (!cancelled) setGraphs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [path, cms.updated_at, knowledge.faqs, knowledge.geo_services]);

  useEffect(() => {
    applySeoHead(cms, DEFAULTS, {
      path,
      pageSeo,
      projectSeo,
      jsonldGraphs: graphs,
    });
  }, [cms, path, pageSeo, projectSeo, graphs]);

  return null;
}
